import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";

/**
 * Public Terms of Service + Privacy Policy page.
 *
 * Referenced from the site footer and the Sign-Up form (users must check the
 * "I have read and agree" box before creating an account). Kept intentionally
 * long and comprehensive so it functions as a real, enforceable agreement for
 * the Start-A-TE platform.
 */
export default function Terms() {
  const { siteSettings } = useAuth();
  const siteName = siteSettings?.site_name || "Start-A-TE";
  const contact = siteSettings?.contact_email || "support@trafficexchange.app";
  const today = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div
      className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-200"
      style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}
      data-testid="terms-page"
    >
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3" data-testid="terms-home-link">
            <img src={siteSettings?.logo_url || LOGO_URL} alt={siteName} className="h-9" />
          </Link>
          <div className="flex items-center gap-2 text-sm">
            <Link to="/login" className="text-blue-700 dark:text-blue-400 font-semibold hover:underline">
              Sign in
            </Link>
            <span className="text-slate-300 dark:text-slate-700">|</span>
            <Link to="/register" className="text-blue-700 dark:text-blue-400 font-semibold hover:underline">
              Create account
            </Link>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-4xl mx-auto px-5 sm:px-6 py-10">
        <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 p-6 sm:p-10">
          <div className="mb-8">
            <p className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-blue-400 mb-2">
              Legal
            </p>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white leading-tight"
              style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}
            >
              Terms of Service &amp; Privacy Policy
            </h1>
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
              Effective date: <span data-testid="terms-effective-date">{today}</span>
            </p>
          </div>

          {/* Table of contents */}
          <nav
            aria-label="Table of contents"
            className="mb-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 p-5"
          >
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">
              Contents
            </p>
            <ol className="grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm list-decimal list-inside text-slate-700 dark:text-slate-300">
              <li><a href="#agreement" className="hover:underline">Agreement to these Terms</a></li>
              <li><a href="#eligibility" className="hover:underline">Eligibility &amp; account registration</a></li>
              <li><a href="#service" className="hover:underline">Description of the service</a></li>
              <li><a href="#credits" className="hover:underline">Credits, cash wallet &amp; referrals</a></li>
              <li><a href="#content" className="hover:underline">Your content &amp; advertised websites</a></li>
              <li><a href="#prohibited" className="hover:underline">Prohibited activities</a></li>
              <li><a href="#surfing" className="hover:underline">Surfing rules &amp; anti-cheat</a></li>
              <li><a href="#suspension" className="hover:underline">Account suspension &amp; termination</a></li>
              <li><a href="#intellectual" className="hover:underline">Intellectual property</a></li>
              <li><a href="#disclaimers" className="hover:underline">Disclaimers &amp; no warranty</a></li>
              <li><a href="#liability" className="hover:underline">Limitation of liability</a></li>
              <li><a href="#indemnity" className="hover:underline">Indemnification</a></li>
              <li><a href="#law" className="hover:underline">Governing law &amp; disputes</a></li>
              <li><a href="#changes" className="hover:underline">Changes to these Terms</a></li>
              <li><a href="#privacy" className="hover:underline">Privacy Policy</a></li>
              <li><a href="#cookies" className="hover:underline">Cookies &amp; tracking</a></li>
              <li><a href="#thirdparty" className="hover:underline">Third-party services</a></li>
              <li><a href="#childrens" className="hover:underline">Children&rsquo;s privacy</a></li>
              <li><a href="#rights" className="hover:underline">Your privacy rights</a></li>
              <li><a href="#contact" className="hover:underline">Contact us</a></li>
            </ol>
          </nav>

          <article className="prose-style space-y-8 text-[15px] leading-relaxed">
            <Section id="agreement" title="1. Agreement to these Terms">
              <p>
                Welcome to {siteName}. These Terms of Service (the &ldquo;Terms&rdquo;) form a binding
                legal agreement between you (&ldquo;you&rdquo;, &ldquo;your&rdquo;, &ldquo;member&rdquo;) and {siteName} (&ldquo;we&rdquo;,
                &ldquo;us&rdquo;, &ldquo;our&rdquo;) governing your access to and use of the website located at
                {" "}{siteName}, together with any related applications, APIs, e-mails, and
                services (collectively, the &ldquo;Service&rdquo;).
              </p>
              <p>
                By clicking &ldquo;Create free account&rdquo;, checking the &ldquo;I have read and agree&rdquo; box on
                the sign-up form, or otherwise accessing or using the Service, you confirm that
                you have read, understood, and agreed to be bound by these Terms and by the
                Privacy Policy set out below. If you do not agree, you must not create an
                account and must stop using the Service.
              </p>
            </Section>

            <Section id="eligibility" title="2. Eligibility & account registration">
              <p>
                You must be at least 16 years old (or the age of digital consent in your
                jurisdiction, whichever is higher) to use the Service. By registering, you
                represent that you meet this requirement and that all information you provide is
                accurate, current, and complete.
              </p>
              <p>
                You are responsible for keeping your login credentials confidential and for all
                activity that occurs under your account. Notify us immediately at{" "}
                <a className="text-blue-700 dark:text-blue-400 underline" href={`mailto:${contact}`}>{contact}</a>{" "}
                if you suspect any unauthorized access. We may refuse registration, suspend
                accounts, or reclaim usernames at our reasonable discretion.
              </p>
              <p>
                One person may hold only one active account unless we grant written permission.
                Duplicate, sock-puppet, or automated accounts are strictly prohibited and may
                result in immediate suspension and forfeiture of all credits and cash balances.
              </p>
            </Section>

            <Section id="service" title="3. Description of the service">
              <p>
                {siteName} is a manual traffic-exchange platform. Members earn credits by
                actively viewing other members&rsquo; websites (&ldquo;surfing&rdquo;) and can spend those
                credits to have their own websites shown to other surfers. The Service also
                includes an advertiser feed, direct messaging, referral tracking, start-page
                advertising, and administrative tools.
              </p>
              <p>
                {siteName} sells credits and membership upgrades directly through the Service.
                Purchases are processed by a third-party payment processor on our behalf, and
                the transaction records are shown in your dashboard. Once a purchase is
                confirmed, the credits or upgrade benefits are applied to your account
                automatically. Refunds are handled case-by-case in line with the refund policy
                on your dashboard.
              </p>
              <p>
                We continuously improve and evolve the Service. Features may be added, changed,
                or removed without prior notice. Credit rewards, plan benefits, timers, and
                referral rates are set by us and may be updated from time to time.
              </p>
            </Section>

            <Section id="credits" title="4. Credits, cash wallet & referrals">
              <p>
                Credits are an in-platform unit used to allocate views to advertised websites.
                Credits have no independent monetary value, cannot be redeemed for cash, and
                are non-transferable between accounts except as explicitly enabled by us (for
                example, referral rewards). Credits and membership upgrades can be purchased
                directly through the Service; pricing and pack sizes are shown on the purchase
                pages and may change from time to time.
              </p>
              <p>
                The cash wallet holds real monetary balances earned through the referral
                program or other promotions. Cash-wallet balances become withdrawable only when
                they meet the minimum payout threshold shown in your dashboard and are subject
                to identity verification and anti-fraud checks. We reserve the right to reverse,
                withhold, or forfeit any credit or cash balance associated with abusive,
                fraudulent, or refunded activity.
              </p>
              <p>
                Our referral program pays a percentage of qualifying activity by users you
                introduce, across up to ten levels deep. Referral commissions are calculated
                automatically and are considered final once they clear anti-fraud review.
                Self-referrals, incentivized clicks, or referral-farm networks are prohibited.
              </p>
            </Section>

            <Section id="content" title="5. Your content & advertised websites">
              <p>
                You retain ownership of the URLs, images, text, and other materials you submit
                to the Service (&ldquo;Your Content&rdquo;). By submitting Your Content, you grant us a
                worldwide, non-exclusive, royalty-free license to host, cache, display,
                transmit, and otherwise use it as necessary to operate and promote the Service,
                including displaying it in the surf rotation and social feed.
              </p>
              <p>
                You represent that you have all rights necessary to submit Your Content and
                that it does not infringe any third-party rights. All submitted websites are
                subject to review by our administrators, who may reject, edit, or unlist any
                site that violates these Terms, applicable law, or the site-quality guidelines
                shown in the dashboard.
              </p>
            </Section>

            <Section id="prohibited" title="6. Prohibited activities">
              <p>You agree not to, and not to allow any third party to:</p>
              <ul className="list-disc list-inside space-y-2 pl-1">
                <li>Advertise or link to content that is illegal, defamatory, hateful, sexually explicit involving minors, or that promotes violence, self-harm, or terrorism.</li>
                <li>Advertise malware, phishing pages, cryptocurrency scams, deceptive &ldquo;get-rich-quick&rdquo; schemes, or pages that trigger unwanted downloads.</li>
                <li>Use bots, scripts, VPN cycling, browser automation, or any technique intended to accumulate credits, referrals, or feed engagement without a real human viewer.</li>
                <li>Attempt to bypass surf timers, anti-cheat verification, captchas, or rate limits.</li>
                <li>Reverse-engineer, decompile, or interfere with the security or performance of the Service.</li>
                <li>Harass, threaten, or impersonate other members, staff, or third parties on the feed or in direct messages.</li>
                <li>Scrape, resell, or redistribute Service data without our written permission.</li>
              </ul>
            </Section>

            <Section id="surfing" title="7. Surfing rules & anti-cheat">
              <p>
                Credits are awarded only when we detect a legitimate human view. This includes
                verifying that the surf page remains focused, that the required timer elapses,
                and that anti-cheat challenges (such as button verifications) are completed by
                a human. Automated or falsified surfing forfeits all credits earned during the
                affected sessions and may result in permanent account termination.
              </p>
              <p>
                Advertised websites must load within a reasonable time frame, must not open
                pop-ups outside of the surf frame, and must not attempt to break out of the
                iframe used to display them. Sites that repeatedly fail our automated quality
                checks may be paused pending review.
              </p>
            </Section>

            <Section id="suspension" title="8. Account suspension & termination">
              <p>
                We may suspend or terminate your account at any time, with or without notice,
                if we believe you have violated these Terms, engaged in fraudulent activity, or
                created legal risk for {siteName} or its members. Upon termination, your right
                to use the Service ends immediately, and outstanding credit and cash balances
                obtained through violating activity may be forfeited.
              </p>
              <p>
                You may close your account at any time from the profile settings page or by
                contacting {contact}. Certain records (transaction history, tax records, abuse
                reports) may be retained for as long as required by law.
              </p>
            </Section>

            <Section id="intellectual" title="9. Intellectual property">
              <p>
                The Service, including its design, source code, database schema, logos,
                trademarks, and documentation, is owned by {siteName} and its licensors and is
                protected by intellectual property laws. Except for the limited rights expressly
                granted to you in these Terms, no license is granted to you to use any of our
                intellectual property.
              </p>
              <p>
                If you believe your copyright has been infringed by content on the Service, you
                may submit a takedown notice to {contact} including the required information
                under applicable law (identification of the copyrighted work, the infringing
                material, your contact details, and a good-faith statement of authority).
              </p>
            </Section>

            <Section id="disclaimers" title="10. Disclaimers & no warranty">
              <p>
                THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT WARRANTIES OF ANY
                KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO
                WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
                NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED,
                ERROR-FREE, SECURE, OR FREE OF HARMFUL COMPONENTS, OR THAT ANY PARTICULAR
                LEVEL OF TRAFFIC, REFERRAL EARNINGS, OR CONVERSIONS WILL RESULT FROM YOUR USE
                OF THE SERVICE.
              </p>
            </Section>

            <Section id="liability" title="11. Limitation of liability">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL {siteName.toUpperCase()},
                ITS AFFILIATES, OR ITS SUPPLIERS BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR
                REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE,
                GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM (A) YOUR USE OF OR
                INABILITY TO USE THE SERVICE, (B) ANY UNAUTHORIZED ACCESS TO OR ALTERATION OF
                YOUR TRANSMISSIONS OR CONTENT, OR (C) ANY OTHER MATTER RELATING TO THE
                SERVICE. OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR
                RELATING TO THE SERVICE IS LIMITED TO THE GREATER OF USD $100 OR THE AMOUNT
                YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
              </p>
            </Section>

            <Section id="indemnity" title="12. Indemnification">
              <p>
                You agree to defend, indemnify, and hold harmless {siteName} and its officers,
                directors, employees, and agents from any claim, damage, loss, liability, cost,
                or expense (including reasonable attorneys&rsquo; fees) arising out of or in any
                way connected with (a) your access to or use of the Service, (b) Your Content,
                or (c) your violation of these Terms or applicable law.
              </p>
            </Section>

            <Section id="law" title="13. Governing law & disputes">
              <p>
                These Terms are governed by the laws of the jurisdiction in which {siteName} is
                established, excluding its conflict-of-law rules. Any dispute arising out of or
                related to these Terms or the Service shall be submitted to the exclusive
                jurisdiction of the competent courts in that jurisdiction, except where
                mandatory consumer-protection law grants you the right to bring proceedings in
                your own country of residence.
              </p>
            </Section>

            <Section id="changes" title="14. Changes to these Terms">
              <p>
                We may modify these Terms from time to time. When we do, we will update the
                &ldquo;Effective date&rdquo; above and, for material changes, we will provide reasonable
                additional notice (for example, through a banner on the site or by email). Your
                continued use of the Service after changes become effective constitutes
                acceptance of the revised Terms.
              </p>
            </Section>

            <div className="border-t border-slate-200 dark:border-slate-800 pt-8">
              <h2
                id="privacy"
                className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white"
                style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}
              >
                Privacy Policy
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                This Privacy Policy is incorporated into and forms part of the Terms.
              </p>
            </div>

            <Section id="privacy-info" title="15. Information we collect">
              <p>To operate the Service we collect the following categories of information:</p>
              <ul className="list-disc list-inside space-y-2 pl-1">
                <li><strong>Account information</strong> — username, email address, password hash, country and time-zone (optional), profile photo, bio.</li>
                <li><strong>Advertiser information</strong> — URLs and metadata for websites, banners, and start pages you submit for promotion.</li>
                <li><strong>Usage &amp; anti-fraud data</strong> — IP address, browser and device metadata, referrer URL, surf sessions, credits earned/spent, feed activity, chat messages you send, timestamps, and anti-cheat verification results.</li>
                <li><strong>Support communications</strong> — the content of any support ticket or email you send us.</li>
              </ul>
              <p>
                Payments for credits and upgrades are processed by a PCI-compliant third-party
                payment processor. We receive a transaction identifier and the amount paid but
                do not receive or store your full card details on our servers.
              </p>
            </Section>

            <Section id="privacy-use" title="16. How we use your information">
              <p>We use your information to:</p>
              <ul className="list-disc list-inside space-y-2 pl-1">
                <li>Provide, secure, and improve the Service, including surf allocation, credit accounting, messaging, and the social feed.</li>
                <li>Verify your identity, prevent fraud, and enforce these Terms.</li>
                <li>Communicate with you about your account, product changes, and (where you have opted in) marketing announcements.</li>
                <li>Comply with legal obligations and respond to lawful requests from authorities.</li>
              </ul>
            </Section>

            <Section id="privacy-share" title="17. When we share your information">
              <p>
                We do not sell your personal information. We share it only with (a) service
                providers that process it on our behalf under a written data-processing
                agreement (for example, cloud hosting, e-mail delivery),
                (b) other members to the extent necessary to display your public profile,
                websites, and feed posts, (c) law-enforcement or regulators when we are legally
                required to do so, and (d) an acquirer in the event of a merger, acquisition,
                or sale of assets, subject to the same privacy commitments.
              </p>
            </Section>

            <Section id="cookies" title="18. Cookies & tracking">
              <p>
                We use strictly-necessary cookies to keep you signed in and secure. We may also
                use analytics cookies to understand aggregated usage patterns. You can control
                cookies through your browser settings; disabling essential cookies will prevent
                the Service from working properly.
              </p>
            </Section>

            <Section id="thirdparty" title="19. Third-party services">
              <p>
                The Service integrates with third-party services including Google (sign-in
                when enabled by the administrator), payment processors, and content-delivery
                networks. Their handling of your data is subject to their own privacy
                policies, and you should review them if you interact with those services.
              </p>
            </Section>

            <Section id="childrens" title="20. Children’s privacy">
              <p>
                The Service is not directed to children under 16. We do not knowingly collect
                personal information from children. If you believe a child has provided us with
                personal information, please contact {contact} and we will promptly delete it.
              </p>
            </Section>

            <Section id="rights" title="21. Your privacy rights">
              <p>
                Depending on where you live, you may have the right to (a) access the personal
                information we hold about you, (b) correct or delete it, (c) object to or
                restrict certain processing, (d) receive your information in a portable format,
                and (e) withdraw consent where processing is based on consent. To exercise any
                of these rights, e-mail {contact} from the address on your account. We will
                respond within the timeframe required by applicable law.
              </p>
              <p>
                We retain personal information for as long as your account is active and for a
                reasonable period afterwards to comply with legal obligations, resolve disputes,
                and enforce our agreements.
              </p>
            </Section>

            <Section id="contact" title="22. Contact us">
              <p>
                Questions, comments, or complaints about these Terms or our privacy practices
                can be sent to{" "}
                <a className="text-blue-700 dark:text-blue-400 underline" href={`mailto:${contact}`}>
                  {contact}
                </a>
                . We aim to acknowledge every message within two (2) business days.
              </p>
            </Section>

            <p className="text-xs text-slate-500 dark:text-slate-400 pt-6 border-t border-slate-200 dark:border-slate-800">
              By using {siteName} you acknowledge that you have read, understood, and agree to
              be bound by both the Terms of Service and the Privacy Policy above.
            </p>
          </article>
        </div>

        <div className="text-center mt-8">
          <Link
            to="/register"
            data-testid="terms-back-to-register"
            className="inline-flex items-center justify-center rounded-full bg-yellow-400 hover:bg-yellow-300 text-yellow-950 font-black px-6 py-3 transition-colors duration-200"
          >
            Continue to sign up
          </Link>
        </div>
      </main>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2
        className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mb-3"
        style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}
      >
        {title}
      </h2>
      <div className="space-y-3 text-slate-700 dark:text-slate-300">{children}</div>
    </section>
  );
}
